import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Record } from 'immutable';

import Button from '@xanadu/ui-shared/components/widgets/Button';

import IconCheck from '../../../../images/icons/misc/check.svg';
import IconUndo from '../../../../images/betslip/icon-undo.svg';

import helpers from '../../../../javascripts/helpers';
import styles from './NewCashout.module.scss';

const UNAVAILABLE_REASONS = {
  event_suspended: 'Event Suspended',
  market_suspended: 'Market Suspended',
  runner_suspended: 'Runner Suspended',
  balanced_position: 'Cashed out',
  low_liquidity: 'Low Liquidity',
  unknown_error: 'Unavailable',
};

const NewCashout = ({
  request,
  runnerId,
  requestCashout,
  processingState,
  offer,
  isLoggedIn,
  setShowCashout,
  eventInRunning,
  offerIndex,
  selectRunner,
  setCashoutSubmitted,
  refbutton,
  requestSubmitTime,
  requestDelay
}) => {
  const [triggerMessage, setTriggerMessage] = useState(false);
  const [showExposureValue, setShowExposureValue] = useState(0);
  const [showAmountValue, setShowAmountValue] = useState(0);
  const [cashoutCountdown, setCashoutCountdown] = useState(0);
  const tickCashoutInterval = useRef(null);
  const messageInterval = useRef(null);
  const { amount, profit, available, status, error } = request;

  const exposure = [amount, profit].includes(null) ? null : amount - profit;

  const currencyFormatter = helpers.localise.currency;

  const tickCashout = () => {
    if (status === 'pending') {
      const now = Date.now().valueOf();
      const deadline = new Date(requestSubmitTime).valueOf() + (requestDelay * 1000);
      const newCountdown = Math.max(0, Math.round(((deadline - now) + 500) / 1000));
      if (newCountdown !== cashoutCountdown) {
        setCashoutCountdown(newCountdown);
      }
      tickCashoutInterval.current = setTimeout(tickCashout, 100);
    }
  };

  let message = 'Trades out all bets on this runner';

  useEffect(() => {
    if (triggerMessage) {
      messageInterval.current = setTimeout(() => {
        setTriggerMessage(false);
        setShowCashout(false);
        setCashoutSubmitted(false);
        setShowExposureValue(0);
        setShowAmountValue(0);
        message = '';
      }, 3000);
    } else {
      return () => clearTimeout(messageInterval.current);
    }
    return () => clearTimeout(messageInterval.current);
  }, [triggerMessage]);

  useEffect(() => {
    if (status === 'pending') {
      tickCashout();
    }
    if (status === 'success' || status === 'failed') {
      setTriggerMessage(true);
      setCashoutSubmitted(true);
      setCashoutCountdown(0);
    }
    return () => clearTimeout(tickCashoutInterval.current);
  }, [status, requestDelay]);

  useEffect(() => () => {
    selectRunner('');
  }, []);

  const handleCashout = () => {
    if (['closed', 'suspended'].includes(status) || !available) {
      return;
    }

    if (!request || ['new', 'failed'].includes(status)) {
      requestCashout({ runnerId });
      setShowExposureValue(exposure);
      setShowAmountValue(amount);
    }
  };

  const handleClose = () => {
    setShowCashout(false);
    selectRunner('');
  };

  const positiveProfit = profit > 0;
  const negativeProfit = profit < 0;

  const profitClass = classNames({
    [styles.profitClass]: true,
    [styles['profitClass--positive']]: positiveProfit,
    [styles['profitClass--negative']]: negativeProfit,
  });

  const buttonConfirm = classNames({
    [styles.buttonConfirm]: true,
    [styles[`buttonConfirm--${processingState}`]]: true,
    [styles['buttonConfirm--disabled']]: request.unavailabilityReason !== undefined || cashoutCountdown > 0,
    [styles['place-bet-btn']]: isLoggedIn,
  });

  const buttonCancel = classNames({
    [styles.buttonCancel]: true,
    [styles[`buttonCancel--${processingState}`]]: true,
    [styles['buttonCancel--disabled']]: cashoutCountdown > 0,
  });

  const containerCashout = classNames({
    [styles.containerCashout]: true,
    [styles['containerCashout--back']]: offer.side === 'back',
    [styles['containerCashout--lay']]: offer.side === 'lay',
  });

  const iconCheckClass = classNames({
    [styles.rowIcon]: true,
    [styles['rowIcon--disable']]: !!request.unavailabilityReason || cashoutCountdown > 0
  });

  const iconUndoClass = classNames({
    [styles.rowIcon]: true,
    [styles['rowIcon--disableUndo']]: cashoutCountdown > 0
  });

  switch (status) {
    case 'success':
      message = (<span className={styles.cashoutFeedback}>Cash out Success</span>);
      break;
    case 'failed':
      message = (<span className={styles.cashoutError}>{error}</span>);
      break;
    case 'new':
      if (!available) {
        message = `Cash Out Unavailable - ${UNAVAILABLE_REASONS[request.unavailabilityReason] || 'Unavailable'}`;
      } else {
        message = 'Trades out all bets on this runner';
      }
      break;
    default:
      message = 'Trades out all bets on this runner';
  }

  const renderAmount = amount !== null ? amount : showAmountValue;
  const renderExposure = exposure !== null ? exposure : showExposureValue;

  return (
    <div ref={refbutton}>
      <div className={containerCashout} >
        <div className={styles.titlesCashout}>
          <span>Exposure</span>
          <span className={styles.valuesCashout}>
            {currencyFormatter(renderExposure, { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className={styles.titlesCashout}>
          <span>Cashout</span>
          <span className={styles.valuesCashout}>
            {!request.unavailabilityReason ? currencyFormatter(renderAmount, { minimumFractionDigits: 2 }) : '---'}
          </span>
        </div>
        <div className={styles.titlesCashout}>
          <span>Profit</span>
          {!request.unavailabilityReason ? (
            <span className={profitClass}>
              {currencyFormatter(profit, { minimumFractionDigits: 2 })}
            </span>
          ) : (
            <span className={styles.valuesCashout}>---</span>
          )}
        </div>
      </div>
      <div className={styles.messageContainer}>{message} </div>
      {(status !== 'success') && (
        <div className={styles.containerOfferActions}>
          <div className={styles.secondAction}>
            <Button
              sizeType="extra-small"
              buttonType="cancel"
              className={buttonCancel}
              disabled={cashoutCountdown > 0}
              onClick={() => handleClose()}
              onKeyUp={(e) => {
                if (e.key === 'Enter') { handleClose(); }
              }}
              dataHook={`betslipcancelcashout-${offerIndex}`}
            >
              <IconUndo className={iconUndoClass} />
              <span className={styles.actionsLabel}>Cancel</span>
            </Button>
          </div>
          <div className={styles.firstAction}>
            <Button
              sizeType="extra-small"
              loading={(status === 'pending' && !eventInRunning)}
              buttonType="confirm"
              disabled={request.unavailabilityReason !== undefined || cashoutCountdown > 0}
              className={buttonConfirm}
              onClick={handleCashout}
              onKeyUp={(e) => {
                if (e.key === 'Enter') { handleCashout(); }
              }}
              dataHook={`betslipcheckmark-${offerIndex}`}
            >
              {eventInRunning && status === 'pending' && cashoutCountdown > 0 ? (
                <Fragment>
                  <img
                    alt="loading"
                    src="/assets/images/icons/live-betting-grey.svg"
                    role="presentation"
                  /><span className={styles.actionsLabel}>{`IN-PLAY DELAY... ${cashoutCountdown}`}</span>
                </Fragment>
              ) : (
                <Fragment>
                  <IconCheck className={iconCheckClass} />
                  <span className={styles.actionsLabel}>Cash Out</span>
                </Fragment>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

NewCashout.propTypes = {
  runner: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  request: PropTypes.shape({
    status: PropTypes.oneOf(['new', 'pending', 'success', 'failed'])
      .isRequired,
    delay: PropTypes.number,
    amount: PropTypes.number,
    profit: PropTypes.number,
    error: PropTypes.string,
    available: PropTypes.bool,
    unavailabilityReason: PropTypes.any,
    submitTime: PropTypes.any
  }),
  runnerId: PropTypes.string.isRequired,
  requestCashout: PropTypes.func.isRequired,
  processingState: PropTypes.string,
  offer: PropTypes.instanceOf(Record).isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  setShowCashout: PropTypes.func.isRequired,
  eventInRunning: PropTypes.bool.isRequired,
  setCashoutSubmitted: PropTypes.func.isRequired,
  offerIndex: PropTypes.number,
  selectRunner: PropTypes.func.isRequired,
  requestSubmitTime: PropTypes.any,
  requestDelay: PropTypes.number,
  refbutton: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object })
  ])
};

export default NewCashout;
